import { Injectable, OnDestroy } from '@angular/core';

import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { AppState } from '../core.state';
import {
  callMicrositeRefreshTokens,
  callMicrositeSessions,
  failMicrositeSessions,
  setMicrositeAuthTokenAuthorizationCode,
  successMicrositeRefreshTokens,
  successMicrositeSessions,
} from './coreAuth.actions';
import { selectMicrositeSessions } from './coreAuth.selectors';
import { Tokens } from './coreAuth.models';

import { of, Subject } from 'rxjs';
import {
  catchError,
  concatMap,
  exhaustMap,
  map,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { Config } from '../../services/config/config.service';
import { SessionClient } from '../../client/session';
import { routerNavigationAction } from '@ngrx/router-store';

@Injectable()
export class MicrositeSessionsEffects implements OnDestroy, OnInitEffects {
  private readonly destroy$ = new Subject<void>();
  public isMobile: boolean;
  private $timeout: ReturnType<typeof setTimeout> | undefined;

  // AuthService
  callMicrositeSessions = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(callMicrositeSessions),
        withLatestFrom(this.store.pipe(select(selectMicrositeSessions))),
        // concatMap (not exhaustMap): ngrxOnInitEffects() dispatches a code-less
        // callMicrositeSessions() at bootstrap, before the router has parsed
        // authorization_code off the URL. That call can still be in flight when
        // the real, code-bearing callMicrositeSessions() (from
        // setAuthorizationCodeSuccess) is dispatched — exhaustMap would silently
        // drop it, dropping the token exchange itself. concatMap queues instead.
        //
        // takeUntil must come AFTER concatMap here (not before, as elsewhere in
        // this file): placed before, it only stops new source emissions —
        // anything already buffered in concatMap's internal queue would still
        // run to completion after ngOnDestroy(). Placed after, destroying the
        // effect also cancels the in-flight request and discards the queue.
        concatMap(([propVal, micrositeSessionState]) => {
          const model = {
            authorization_code:
              micrositeSessionState.AuthToken.authorization_code,
          };
          return this.sessionClient.session(model).pipe(
            map((data) => {
              return successMicrositeSessions({
                response: data,
                requestId: propVal.requestId,
              });
            }),
            catchError((error) => {
              // TODO: Normalize error using normalizeError() once backend error format is confirmed
              return of(
                failMicrositeSessions({ error, requestId: propVal.requestId }),
              );
            }),
          );
        }),
        takeUntil(this.destroy$),
      );
    },
    { dispatch: true },
  );

  successMicrositeSessions = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(successMicrositeSessions),
        takeUntil(this.destroy$),
        tap((propVal) => {
          this.initTokens(propVal.response?.auth_tokens);
        }),
      );
    },
    { dispatch: false },
  );

  // AuthTokenService
  routerNavigationAction = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(routerNavigationAction),
        withLatestFrom(this.store.pipe(select(selectMicrositeSessions))),
        exhaustMap(([propVal, micrositeSessionState]) => {
          // Access queryParams from the router state
          const authCode = (<any>propVal).payload.routerState.queryParams
            ?.authorization_code;
          // The query param survives every redirect this app does on the way to its
          // final route (guard redirect, then the default-sport redirect), so this
          // effect sees the SAME authorization_code across multiple NavigationEnds.
          // The code is single-use server-side — only act on it the first time it's
          // seen, or the 2nd `callMicrositeSessions()` call re-sends an already-spent
          // code and 401s.
          if (
            authCode !== undefined &&
            authCode !== micrositeSessionState.AuthToken.authorization_code
          ) {
            console.log('Found authorization code');
            return [
              setMicrositeAuthTokenAuthorizationCode({
                authorization_code: authCode,
              }),
            ];
          }
          return of(); // Return empty observable if no new authorization code is present
        }),
      );
    },
    { dispatch: true },
  );

  // NEW: Trigger callMicrositeSessions after authorization code is set
  setAuthorizationCodeSuccess = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(setMicrositeAuthTokenAuthorizationCode),
        map(() => callMicrositeSessions()),
      );
    },
    { dispatch: true },
  );

  callMicrositeRefreshTokens = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(callMicrositeRefreshTokens),
        withLatestFrom(this.store.pipe(select(selectMicrositeSessions))),
        exhaustMap(([propVal, micrositeSessionState]) => {
          const body = {
            refresh_token:
              micrositeSessionState.AuthToken.tokens?.refresh_token,
          };
          return this.sessionClient.refresh(body).pipe(
            map((data) => {
              return successMicrositeRefreshTokens({ response: data });
            }),
          );
        }),
        // takeUntil after exhaustMap (same reasoning as callMicrositeSessions
        // above): destroying the effect must also cancel an in-flight refresh
        // call, not just stop new dispatches from starting one.
        takeUntil(this.destroy$),
      );
    },
    { dispatch: true },
  );

  successMicrositeRefreshTokens = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(successMicrositeRefreshTokens),
        tap((propVal) => {
          this.initTokens(propVal.response);
        }),
      );
    },
    { dispatch: false },
  );

  constructor(
    public actions$: Actions,
    public store: Store<AppState>,
    private sessionClient: SessionClient,
    private _config: Config,
  ) {
    this.isMobile = this._config.CONFIG.isMobile;
  }

  // NgRX lifecycle hook - dispatches initial action
  //
  // Reads authorization_code directly from the raw browser URL (window.location.search)
  // rather than waiting for the Router to carry it through to a NavigationEnd. This runs
  // at effects registration, before the Router performs its initial navigation — so it
  // sees the URL exactly as the SSO handoff delivered it, unaffected by any guard/redirect
  // (PoolsDefaultRouteGuard, TicketsRouteGuard, the app's ** wildcard, etc.) that may or may
  // not preserve query params on the way to wherever the user ends up landing.
  //
  // routerNavigationAction (below) is kept as a fallback for a genuinely NEW
  // authorization_code arriving via an in-SPA navigation after boot (no full reload) — its
  // dedup against the already-stored code means it's a no-op if this bootstrap read already
  // consumed the same one.
  ngrxOnInitEffects(): Action {
    const authCode = new URLSearchParams(window.location.search).get(
      'authorization_code',
    );
    if (authCode) {
      console.log('Found authorization code');
      return setMicrositeAuthTokenAuthorizationCode({
        authorization_code: authCode,
      });
    }
    return callMicrositeSessions();
  }

  initTokens(new_tokens: Tokens | undefined) {
    if (new_tokens) {
      const time_now = new Date();
      // expires_at is typed optional; not independently verified non-null here — pre-existing, unguarded call site
      const token_expire = new Date(new_tokens.expires_at!);
      const expiration = token_expire.getTime() - time_now.getTime() - 30000;

      clearTimeout(this.$timeout);
      this.$timeout = setTimeout(() => {
        this.store.dispatch(callMicrositeRefreshTokens());
      }, expiration);
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.$timeout);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
